import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

import placeholder from 'lib/placeholder';

import { useUser } from 'providers/UserProvider';

import useDialog from './useDialog';

import COUPON from 'constants/coupons';

const ExclusiveForAppsAlertDialog = dynamic(
  () => import('components/ExclusiveForAppDialog'),
  { ssr: false }
);

const LoginDialog = dynamic(() => import('components/LoginDialog'), {
  ssr: false,
});

const formatGaEventActionByVisibility = (prefix, visibility) => {
  const postfixes = {
    [COUPON.VISIBILITY_STATUS.ALL]: '',
    [COUPON.VISIBILITY_STATUS.APP_ONLY]: '__app__exclusive',
    [COUPON.VISIBILITY_STATUS.LOGGED_ONLY]: '__exclusive',
    [COUPON.VISIBILITY_STATUS.UNLOGGED_ONLY]: '',
  };

  const postfix = postfixes[visibility] || '';

  return prefix + postfix;
};

const useExclusiveCoupon = () => {
  const router = useRouter();
  const { isLoggedIn, loadingLoggedInUser } = useUser();
  const { showDialog } = useDialog();

  const isCouponBlocked = useCallback(
    (visibility) => {
      /**
       * The visibility of `UNLOGGED_ONLY` already exists but your logic is not
       * implemented yet.
       */
      if (
        visibility === COUPON.VISIBILITY_STATUS.ALL ||
        visibility === COUPON.VISIBILITY_STATUS.UNLOGGED_ONLY
      ) {
        return false;
      }

      if (visibility === COUPON.VISIBILITY_STATUS.APP_ONLY) {
        return true;
      }

      return !loadingLoggedInUser && !isLoggedIn;
    },
    [loadingLoggedInUser, isLoggedIn]
  );

  const onUnloggedUserTriesOpenExclusiveCoupon = useCallback(
    (visibility, openCouponPickerCallback) => {
      if (isLoggedIn && visibility === COUPON.VISIBILITY_STATUS.LOGGED_ONLY) {
        openCouponPickerCallback();
        return;
      }

      if (visibility === COUPON.VISIBILITY_STATUS.APP_ONLY) {
        showDialog(ExclusiveForAppsAlertDialog, { domain: COUPON.DOMAIN });
        return;
      }

      showDialog(LoginDialog, {
        greetingsMessage: placeholder('titles.exclusiveCouponSocialSignin'),
        onSuccess: () => {
          openCouponPickerCallback();
        },
      });
    },
    [loadingLoggedInUser, isLoggedIn, router]
  );

  return {
    isCouponBlocked,
    formatGaEventActionByVisibility,
    onUnloggedUserTriesOpenExclusiveCoupon,
  };
};

export default useExclusiveCoupon;
